<template>               
  <div class="container-fluid">
    <div class="d-flex align-items-start m-2 gap-1">
      <div class="">
        <label class="text-muted fs-sm my-1">Language</label>
        <select class="form-select form-select-sm" v-model="selectedLanguage">
          <option disabled value="">Please select one</option>
          <option v-for="(u,i) in availableLanguages" :key="i" :value="u">{{u.label}}</option>
        </select>
      </div>
      <ContentTypeChooser v-if="queryDefinition" :queryDefinition="queryDefinition" />

      <button type="button" class="btn btn-outline-primary btn-sm ms-auto align-self-center" @click="copyToClipboard()" :disabled="isCopying">
        <i class="bi bi-clipboard"></i> {{isCopying?"Copied!":"Copy"}}
      </button>
    </div>
  
    <!-- Use ref to target specific element for Prism highlighting -->
    <div class="row fs-sm" ref="codeContainer" style="min-height:300px">
      <pre class="line-numbers"><code :class="languageStyleclass" ref="codeElement">{{ sampleCode }}</code></pre>
    </div>
  </div>
</template>

<script>
import {VcQueryLanguageGenerator} from "/src/visualcrossing-core/vcquerylanguagegenerator";
import Prism from 'prismjs';
import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';
import 'prismjs/themes/prism-tomorrow.css';

// Core language components that others depend on
import 'prismjs/components/prism-markup'; // HTML/XML
import 'prismjs/components/prism-clike'; // C-like syntax base

// Language-specific components (order matters for dependencies)
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-markup-templating'; // Required for PHP
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-r';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-bash'; // For cURL

import ContentTypeChooser from '/src/components/ContentTypeChooser.vue'
import {VcListUtils} from '/src/visualcrossing-core/vclistutils'
import {VcClipboardUtils} from '/src/visualcrossing-core/vcclipboardutils'

export default {
  name: 'ApiQuery',
  props: {
    queryDefinition: Object,  
    sessionState: Object,
    location: Object,
  },
  data() {
    return {
      isCopying: false,
      selectedLanguage: VcQueryLanguageGenerator.LANGUAGES[0],
    }
  },
  methods: {
    copyToClipboard() {
      let me = this;
      if (window["gtag"]) {
        gtag('event', 'api_copy_' + this.selectedLanguageLabel, { method: 'Vc' });
      }
      VcClipboardUtils.copyToClipboard(this.sampleCode);

      me.isCopying = true;

      setTimeout(() => {
        me.isCopying = false;
      }, 2000);
    },

    setLanguage(v) {
      this.selectedLanguage = v;
    },

    highlightCode() {
      // Wait for DOM update, then highlight the specific code element
      this.$nextTick(() => {
        if (this.$refs.codeElement) {
          // Remove any existing highlighting
          this.$refs.codeElement.removeAttribute('data-highlighted');
          
          // Apply highlighting to the specific element
          Prism.highlightElement(this.$refs.codeElement);
        }
      });
    }
  },

  computed: {
    userKey() {
      return this.sessionState && this.sessionState.apiKey
    },
 
    resolvedQueryDefinition() {
      var me = this;
      if (!me.queryDefinition) return null;
      var result = JSON.parse(JSON.stringify(me.queryDefinition));
      if (me.location) {
        result.location = me.location.location;
        result.name = me.location.name;
      }
      return result;
    },

    languageStyleclass() {
      var lang = (this.selectedLanguage && (this.selectedLanguage.class || this.selectedLanguage.id)) || "none";
      return "language-" + lang;
    },

    availableLanguages() {
      return VcQueryLanguageGenerator.LANGUAGES || [];
    },
    
    selectedLanguageLabel() {
      return (this.selectedLanguage && this.selectedLanguage.label) || "Select language";
    },

    sampleCode() {
      var me = this;
      if (!me.selectedLanguage) return "";
      return VcQueryLanguageGenerator.generateCode(me.selectedLanguage.id, me.resolvedQueryDefinition, me.userKey)
    }
  },

  watch: {
    // Watch for changes that should trigger re-highlighting
    sampleCode() {
      this.highlightCode();
    },
    
    selectedLanguage() {
      this.highlightCode();
    },

    resolvedQueryDefinition: {
      handler() {
        this.highlightCode();
      },
      deep: true
    }
  },

  mounted() {
    // Initial highlighting
    this.highlightCode();
  },

  components: {
    ContentTypeChooser
  }
}
</script>

<style scoped>
/* Ensure proper styling for Prism */
pre[class*="language-"] {
  margin: 0;
  padding: 1em;
  overflow: auto;
}

code[class*="language-"] {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
}
</style>
